// ─────────────────────────────────────────────────────────────────
// Glass-i — Other Charge Rule Add / Edit popup (Stage 6OC.1).
//
// VERTICAL step wizard over the approved reference sections:
//   Step 1 · Basic Details   — Charge Name, Mode, Value By, Rule Value, Display As
//   Step 2 · Applicability   — Material Types, Glass Types, Categories,
//                              Thickness, Auto Include, Taxable, Active
//   Step 3 · Condition       — Condition Type, Dimension Limit, Area Basis,
//                              Area Limit, Charge Base, GST %, HSN/SAC
//   Step 4 · Area Slabs      — Slab 1/2/3 area + value
//
// GST % is a DROPDOWN bound to the tax-master GST rates (dropdowns.gstRates).
//
// Sort Order and Charge Code are HIDDEN (per request): Sort Order
// defaults to 0; Charge Code auto-derives from the Charge Name so the
// backend (which requires it) still receives a value.
//
// Every step stays MOUNTED (display toggle) so the single Form keeps all
// values across steps. Backend contract UNCHANGED — engine fields ship
// safe defaults (ADD_DEFAULTS), merged into the payload on submit.
//
// Master-driven dropdowns (material / glass / thickness / category / GST)
// come ONLY from props.dropdowns — never hardcoded.
// ─────────────────────────────────────────────────────────────────

import { useEffect, useMemo, useState } from 'react';
import {
  Button, Col, Form, Input, InputNumber, message, Modal,
  Row, Select, Space, Steps, Switch
} from 'antd';
import { buildDropdownStateProps } from '../common/dropdownLoaderState';
import {
  validateOtherChargeRule,
  buildOtherChargeRulePayload,
  FORMULA_CODES,
  SOURCE_COLUMNS,
  UOMS,
  QUANTITY_BASES,
  SHOW_RESULT_MODES,
  formulaCodeLabel,
  sourceColumnLabel,
  uomLabel,
  quantityBasisLabel,
  showResultModeLabel
} from './otherChargesHelpers';
import '../masters/customers/CustomerTabbedDrawer.css';

// ── Internal rule-engine enums (allowed hardcoded constants) ──────
const MODE_OPTIONS = [
  { value: 'INR', label: 'INR (₹)' },
  { value: 'PERCENT', label: 'Percent (%)' },
  { value: 'MULTIPLIER', label: 'Multiplier (×)' }
];
const VALUE_BY_OPTIONS = [
  { value: 'PER_SQM_MM', label: 'Per SQM × thickness (mm)' },
  { value: 'PER_MM', label: 'Per mm' },
  { value: 'PER_SQM', label: 'Per SQM' },
  { value: 'PER_SQFT', label: 'Per SQFT' },
  { value: 'PER_PCS', label: 'Per piece' },
  { value: 'PER_RFT', label: 'Per RFT' },
  { value: 'PER_RMT', label: 'Per RMT' },
  { value: 'PER_MTR', label: 'Per metre' },
  { value: 'FIXED', label: 'Fixed' },
  { value: 'CONDITION_BASED', label: 'Condition based' }
];
const CONDITION_TYPE_OPTIONS = [
  { value: 'ALWAYS', label: 'Always' },
  { value: 'ANY_SIDE_EXCEEDS', label: 'Any side exceeds' },
  { value: 'WIDTH_EXCEEDS', label: 'Width exceeds' },
  { value: 'HEIGHT_EXCEEDS', label: 'Height exceeds' },
  { value: 'AREA_EXCEEDS', label: 'Area exceeds' },
  { value: 'AREA_SLAB', label: 'Area slab' },
  { value: 'SIZE_OR_AREA', label: 'Size or area' },
  { value: 'SIZE_AND_AREA', label: 'Size and area' }
];
const AREA_BASIS_OPTIONS = [
  { value: 'LINE_SQM', label: 'Line SQM' },
  { value: 'CHARGEABLE_SQM', label: 'Chargeable SQM' }
];
// Stage 6OC.2 — the base a Percent/Multiplier charge computes on.
const COMPUTE_BASE_OPTIONS = [
  { value: 'RUNNING_TOTAL', label: 'Running total (basic + earlier charges)' },
  { value: 'LINE_BASIC', label: 'Base rate (line basic only)' }
];
// ── Stage 6B.8A-3O — Formula + Area-Basis/UOM descriptor options ──
const FORMULA_OPTIONS = FORMULA_CODES.map((c) => ({ value: c, label: formulaCodeLabel(c) }));
const SOURCE_COLUMN_OPTIONS = SOURCE_COLUMNS.map((c) => ({ value: c, label: sourceColumnLabel(c) }));
const UOM_OPTIONS = UOMS.map((c) => ({ value: c, label: uomLabel(c) }));
const QUANTITY_BASIS_OPTIONS = QUANTITY_BASES.map((c) => ({ value: c, label: quantityBasisLabel(c) }));
const SHOW_RESULT_OPTIONS = SHOW_RESULT_MODES.map((c) => ({ value: c, label: showResultModeLabel(c) }));
const CHARGE_BASE_OPTIONS = [
  { value: 'PERCENT_OF_LINE_BASIC', label: '% of line basic' },
  { value: 'FIXED_PER_PCS', label: 'Fixed per piece' },
  { value: 'FIXED_PER_SQM', label: 'Fixed per SQM' },
  { value: 'FIXED_PER_SQFT', label: 'Fixed per SQFT' },
  { value: 'FIXED_PER_DOCUMENT', label: 'Fixed per document' }
];
const DISPLAY_AS_OPTIONS = [
  { value: 'LINE_WISE', label: 'Line-wise' },
  { value: 'THICKNESS_WISE', label: 'Thickness-wise' },
  { value: 'SINGLE_TOTAL', label: 'Single total' }
];

const STEP_TITLES = ['Basic Details', 'Applicability', 'Condition & Calc', 'Area Slabs'];
const LAST = STEP_TITLES.length - 1;

const ADD_DEFAULTS = Object.freeze({
  applicableMaterialTypeCodes: ['ALL'],
  glassTypeCodes: ['ALL'],
  thicknessCodes: ['ALL'],
  categoryCodes: ['ALL'],
  glassFamilyCodes: ['ALL'],
  moduleScope: 'ALL',
  applyOn: 'ALL',
  mode: 'INR',
  valueBy: 'FIXED',
  ruleValue: 0,
  conditionType: 'ALWAYS',
  dimensionLimitMm: 0,
  areaBasis: 'LINE_SQM',
  areaLimitSqm: 0,
  chargeBase: 'FIXED_PER_PCS',
  displayAs: 'LINE_WISE',
  formulaCode: 'FIXED',
  sourceColumn: 'CHARGEABLE',
  uom: 'SQM',
  quantityBasis: 'ALL_QTY',
  showResultMode: 'THICKNESS_WISE',
  computeBase: 'RUNNING_TOTAL',
  polishTypeCode: 'ALL',
  displayGroup: null,
  roundTo: 2,
  slab1Area: 0, slab1Value: 0,
  slab2Area: 0, slab2Value: 0,
  slab3Area: 0, slab3Value: 0,
  taxable: true,
  gstRate: 18,
  hsnSac: '',
  autoInclude: true,
  allowRuntimeRate: true,
  allowResultRateEdit: true,
  active: true,
  includeSheetScrap: true,
  sortOrder: 0
});

function toOptions(list) {
  return (Array.isArray(list) ? list : []).map((item) => ({
    value: item.code,
    label: item.label != null ? String(item.label) : String(item.code)
  }));
}
function deriveCode(name) {
  return String(name || '')
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 40);
}
function summarize(v) {
  const amt = Number(v.ruleValue) || 0;
  let how;
  switch (v.chargeBase) {
    case 'PERCENT_OF_LINE_BASIC': how = `adds ${amt}% of each item's value`; break;
    case 'FIXED_PER_PCS': how = `adds ₹${amt} per piece`; break;
    case 'FIXED_PER_SQM': how = `adds ₹${amt} per SQM`; break;
    case 'FIXED_PER_SQFT': how = `adds ₹${amt} per SQFT`; break;
    case 'FIXED_PER_DOCUMENT': how = `adds ₹${amt} once per document`; break;
    default: how = 'adds a charge';
  }
  // Configured area slabs override the flat value once a line's area matches a
  // slab — surface that so the summary isn't misleading.
  const slabs = [
    [Number(v.slab1Area) || 0, Number(v.slab1Value) || 0],
    [Number(v.slab2Area) || 0, Number(v.slab2Value) || 0],
    [Number(v.slab3Area) || 0, Number(v.slab3Value) || 0]
  ].filter(([a]) => a > 0);
  const unit = v.mode === 'PERCENT' ? '%' : '₹';
  const slabNote = slabs.length
    ? ` Area slabs override this: ${slabs.map(([a, val]) => `≥${a} SQM → ${unit === '%' ? `${val}%` : `₹${val}`}`).join(', ')}.`
    : '';
  const tax = v.taxable ? ` Taxable at ${Number(v.gstRate) || 0}% GST.` : ' Not taxable.';
  return `${v.chargeName ? `“${v.chargeName}” ` : 'This charge '}${how}.${slabNote}${tax}`;
}

// Reference-style section card: bordered, light header bar, padded body.
function Section({ title, children }) {
  return (
    <div style={{ border: '1px solid #e2e8f0', borderRadius: 12, overflow: 'hidden', background: '#fff' }}>
      <div style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0', padding: '8px 12px', fontSize: 13, fontWeight: 600, color: '#0f172a' }}>
        {title}
      </div>
      <div style={{ padding: 12 }}>{children}</div>
    </div>
  );
}

export default function OtherChargeRuleModal({
  open,
  mode = 'create',
  initialValues,
  dropdowns,
  dropdownState,
  onSubmit,
  onCancel,
  submitting = false
}) {
  const [form] = Form.useForm();
  const [step, setStep] = useState(0);
  const [codeTouched, setCodeTouched] = useState(false);

  const taxable = Form.useWatch('taxable', form);
  // Stage 6OC.2 — "Compute on" only affects Percent/Multiplier charges; shown
  // for those modes (flat/area charges don't read a base).
  const watchMode = Form.useWatch('mode', form);
  const showComputeBase = watchMode === 'PERCENT' || watchMode === 'MULTIPLIER';

  const dd = dropdowns && typeof dropdowns === 'object' ? dropdowns : {};
  const ddState = dropdownState && typeof dropdownState === 'object' ? dropdownState : {};

  const materialOptions = useMemo(() => toOptions(dd.materialTypes), [dd.materialTypes]);
  const glassOptions = useMemo(() => toOptions(dd.glassTypes), [dd.glassTypes]);
  const thicknessOptions = useMemo(() => toOptions(dd.thicknesses), [dd.thicknesses]);
  const categoryOptions = useMemo(() => toOptions(dd.categories), [dd.categories]);
  const familyOptions = useMemo(() => toOptions(dd.glassFamilies), [dd.glassFamilies]);
  const polishOptions = useMemo(() => toOptions(dd.polishTypes), [dd.polishTypes]);
  // GST % dropdown — bound to the tax-master GST rates.
  const gstRateOptions = useMemo(
    () => (Array.isArray(dd.gstRates) ? dd.gstRates : []).map((g) => ({
      value: Number(g.value),
      label: g.label != null ? String(g.label) : `${g.value}%`
    })),
    [dd.gstRates]
  );

  // Hydrate on open.
  useEffect(() => {
    if (!open) return;
    setStep(0);
    form.resetFields();
    const isEdit = mode !== 'create' && initialValues && typeof initialValues === 'object';
    form.setFieldsValue(isEdit ? { ...ADD_DEFAULTS, ...initialValues } : { ...ADD_DEFAULTS });
    setCodeTouched(Boolean(isEdit && initialValues.chargeCode));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, mode, initialValues]);

  // Charge Name → auto Charge Code (hidden).
  const onNameChange = (e) => {
    const v = e.target.value;
    form.setFieldValue('chargeName', v);
    if (!codeTouched) form.setFieldValue('chargeCode', deriveCode(v));
  };

  const collect = () => ({ ...ADD_DEFAULTS, ...form.getFieldsValue(true) });

  // Per-step validation before advancing.
  const stepFields = () => {
    if (step === 0) return ['chargeName'];
    if (step === 2) return ['gstRate'];
    return [];
  };
  const next = async () => {
    const f = stepFields();
    try {
      if (f.length) await form.validateFields(f);
    } catch (_e) {
      return;
    }
    setStep((s) => Math.min(LAST, s + 1));
  };
  const back = () => setStep((s) => Math.max(0, s - 1));

  const submit = async () => {
    try {
      await form.validateFields();
    } catch (_e) {
      return;
    }
    const values = collect();
    const { ok, errors } = validateOtherChargeRule(values);
    if (!ok) {
      const fields = Object.keys(errors).map((name) => ({ name, errors: [errors[name]] }));
      form.setFields(fields);
      message.error(Object.values(errors)[0]);
      return;
    }
    const payload = buildOtherChargeRulePayload(values);
    if (onSubmit) await onSubmit(payload);
  };

  const preview = () => {
    Modal.info({
      title: 'Rule preview',
      content: <div style={{ fontSize: 13 }}>{summarize(collect())}</div>,
      okText: 'Close'
    });
  };

  const title = mode === 'create' ? 'Add Other Charge Rule' : 'Edit Other Charge Rule';
  const show = (i) => ({ display: step === i ? 'block' : 'none' });

  return (
    <Modal
      title={title}
      open={open}
      onCancel={onCancel}
      width={940}
      centered
      destroyOnClose
      maskClosable={false}
      className="erp-customer-modal"
      styles={{ body: { padding: 16, background: '#fafafa', maxHeight: '74vh', overflowY: 'auto' } }}
      footer={
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Button onClick={onCancel}>Cancel</Button>
          <Space>
            {step > 0 ? <Button onClick={back}>Back</Button> : null}
            {step < LAST
              ? <Button type="primary" onClick={next}>Next</Button>
              : (
                <>
                  <Button onClick={preview}>Preview Rule</Button>
                  <Button type="primary" loading={submitting} onClick={submit}>
                    {mode === 'create' ? 'Save Rule' : 'Save Changes'}
                  </Button>
                </>
              )}
          </Space>
        </div>
      }
    >
      <Form form={form} layout="vertical" preserve className="erp-customer-drawer__form">
        {/* Hidden — Sort Order + Charge Code (auto) + engine defaults. */}
        <Form.Item name="sortOrder" hidden><InputNumber /></Form.Item>
        <Form.Item name="chargeCode" hidden><Input /></Form.Item>
        <Form.Item name="moduleScope" hidden><Input /></Form.Item>
        <Form.Item name="applyOn" hidden><Input /></Form.Item>
        <Form.Item name="roundTo" hidden><InputNumber /></Form.Item>
        <Form.Item name="allowRuntimeRate" hidden valuePropName="checked"><Switch /></Form.Item>
        <Form.Item name="allowResultRateEdit" hidden valuePropName="checked"><Switch /></Form.Item>

        <div style={{ display: 'flex', gap: 20, alignItems: 'flex-start' }}>
          {/* Vertical step rail */}
          <div style={{ flex: '0 0 168px', position: 'sticky', top: 0 }}>
            <Steps
              direction="vertical"
              size="small"
              current={step}
              items={STEP_TITLES.map((t) => ({ title: t }))}
            />
          </div>

          {/* Step content */}
          <div style={{ flex: 1, minWidth: 0 }}>
            {/* Step 1 · Basic Details */}
            <div style={show(0)}>
              <Section title="Basic Details">
                <Row gutter={16}>
                  <Col xs={24} md={12}>
                    <Form.Item label="Charge Name" name="chargeName"
                      rules={[{ required: true, message: 'Charge name is required' }]}>
                      <Input maxLength={150} placeholder="e.g. Tempering Charge" onChange={onNameChange} />
                    </Form.Item>
                  </Col>
                  <Col xs={12} md={6}>
                    <Form.Item label="Mode" name="mode"><Select options={MODE_OPTIONS} /></Form.Item>
                  </Col>
                  <Col xs={12} md={6}>
                    <Form.Item label="Value By" name="valueBy"><Select options={VALUE_BY_OPTIONS} /></Form.Item>
                  </Col>
                  <Col xs={12} md={6}>
                    <Form.Item label="Rule Value" name="ruleValue"
                      rules={[{
                        validator: (_, v) => Number(v) >= 0 ? Promise.resolve() : Promise.reject(new Error('Enter 0 or more'))
                      }]}>
                      <InputNumber min={0} step={0.01} style={{ width: '100%' }} />
                    </Form.Item>
                  </Col>
                  <Col xs={12} md={6}>
                    <Form.Item label="Display As" name="displayAs"><Select options={DISPLAY_AS_OPTIONS} /></Form.Item>
                  </Col>
                  {showComputeBase ? (
                    <Col xs={24} md={12}>
                      <Form.Item label="Compute on" name="computeBase"
                        tooltip="Running total: the % stacks on the line basic plus charges added before it. Base rate: the % uses the line's own basic only (no compounding).">
                        <Select options={COMPUTE_BASE_OPTIONS} />
                      </Form.Item>
                    </Col>
                  ) : (
                    <Form.Item name="computeBase" hidden><Input /></Form.Item>
                  )}
                </Row>
              </Section>
            </div>

            {/* Step 2 · Applicability */}
            <div style={show(1)}>
              <Section title="Applicability">
                <Row gutter={16}>
                  <Col xs={24} md={12}>
                    <Form.Item label="Applicable Material Types" name="applicableMaterialTypeCodes"
                      tooltip="Leave “All” to apply to every material type.">
                      <Select mode="multiple" allowClear options={materialOptions} placeholder="All material types"
                        optionFilterProp="label" maxTagCount="responsive" {...buildDropdownStateProps(ddState.materialTypes)} />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={12}>
                    <Form.Item label="Glass Types" name="glassTypeCodes">
                      <Select mode="multiple" allowClear options={glassOptions} placeholder="All glass types"
                        optionFilterProp="label" maxTagCount="responsive" {...buildDropdownStateProps(ddState.glassTypes)} />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={6}>
                    <Form.Item label="Categories" name="categoryCodes"
                      tooltip="Glass categories from the Glass Category master.">
                      <Select mode="multiple" allowClear options={categoryOptions} placeholder="All categories"
                        optionFilterProp="label" maxTagCount="responsive" {...buildDropdownStateProps(ddState.categories)} />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={6}>
                    <Form.Item label="Thickness" name="thicknessCodes">
                      <Select mode="multiple" allowClear options={thicknessOptions} placeholder="All thicknesses"
                        optionFilterProp="label" maxTagCount="responsive" {...buildDropdownStateProps(ddState.thicknesses)} />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={6}>
                    <Form.Item label="Glass Family" name="glassFamilyCodes"
                      tooltip="Glass COLOUR family (clear / tinted / reflective / coated / mirror …). Use for colour-scoped charges — e.g. a Color Shape charge set to tinted + reflective bills only on coloured glass, not clear. Leave “All” to ignore colour.">
                      <Select mode="multiple" allowClear options={familyOptions} placeholder="All families"
                        optionFilterProp="label" maxTagCount="responsive" {...buildDropdownStateProps(ddState.glassFamilies)} />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={6}>
                    <Form.Item label="Polish Type" name="polishTypeCode"
                      tooltip="Bind this charge to ONE polish. 'All' applies to every product; a specific polish (e.g. CEP) applies ONLY to products with that polish and excludes all others.">
                      <Select showSearch options={polishOptions} placeholder="All polishes"
                        optionFilterProp="label" {...buildDropdownStateProps(ddState.polishTypes)} />
                    </Form.Item>
                  </Col>
                  <Col xs={8} md={4}>
                    <Form.Item label="Auto Include" name="autoInclude" valuePropName="checked">
                      <Switch checkedChildren="Yes" unCheckedChildren="No" />
                    </Form.Item>
                  </Col>
                  <Col xs={8} md={4}>
                    <Form.Item label="Taxable" name="taxable" valuePropName="checked">
                      <Switch checkedChildren="Yes" unCheckedChildren="No" />
                    </Form.Item>
                  </Col>
                  <Col xs={8} md={4}>
                    <Form.Item label="Active" name="active" valuePropName="checked">
                      <Switch checkedChildren="On" unCheckedChildren="Off" />
                    </Form.Item>
                  </Col>
                  <Col xs={12} md={6}>
                    <Form.Item label="Apply to SHEET scrap" name="includeSheetScrap" valuePropName="checked"
                      tooltip="A SHEET line is raw stock. When ON, this charge applies to the SHEET's scrap value. Turn OFF for product surcharges (e.g. Energy per-mm, Jumbo, Shape) so they never bill a stock sheet. Glass lines are unaffected either way.">
                      <Switch checkedChildren="Yes" unCheckedChildren="No" />
                    </Form.Item>
                  </Col>
                </Row>
              </Section>
            </div>

            {/* Step 3 · Condition & Calculation */}
            <div style={show(2)}>
              <Section title="Condition & Calculation">
                <Row gutter={16}>
                  <Col xs={12} md={6}>
                    <Form.Item label="Condition Type" name="conditionType"><Select options={CONDITION_TYPE_OPTIONS} /></Form.Item>
                  </Col>
                  <Col xs={12} md={6}>
                    <Form.Item label="Dimension Limit (mm)" name="dimensionLimitMm">
                      <InputNumber min={0} step={1} style={{ width: '100%' }} />
                    </Form.Item>
                  </Col>
                  <Col xs={12} md={6}>
                    <Form.Item label="Area Basis" name="areaBasis"><Select options={AREA_BASIS_OPTIONS} /></Form.Item>
                  </Col>
                  <Col xs={12} md={6}>
                    <Form.Item label="Area Limit (SQM)" name="areaLimitSqm">
                      <InputNumber min={0} step={0.0001} style={{ width: '100%' }} />
                    </Form.Item>
                  </Col>
                  <Col xs={12} md={6}>
                    <Form.Item label="Charge Base" name="chargeBase"><Select options={CHARGE_BASE_OPTIONS} /></Form.Item>
                  </Col>
                  <Col xs={12} md={6}>
                    <Form.Item label="GST %" name="gstRate"
                      tooltip="Bound to the tax-master GST rates."
                      rules={[{
                        validator: (_, v) => {
                          if (v === undefined || v === null || v === '') return Promise.resolve();
                          const g = Number(v);
                          return g >= 0 && g <= 28 ? Promise.resolve() : Promise.reject(new Error('0 – 28 only'));
                        }
                      }]}>
                      <Select
                        options={gstRateOptions}
                        placeholder="Select GST rate"
                        disabled={taxable === false}
                        {...buildDropdownStateProps(ddState.gstRates)}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={12} md={6}>
                    <Form.Item label="HSN / SAC" name="hsnSac"><Input maxLength={20} placeholder="e.g. 9988" /></Form.Item>
                  </Col>
                </Row>
              </Section>

              <div style={{ height: 12 }} />

              <Section title="Formula & Area Basis">
                <Row gutter={16}>
                  <Col xs={12} md={8}>
                    <Form.Item label="Formula" name="formulaCode"
                      tooltip="How the charge amount is computed at runtime.">
                      <Select options={FORMULA_OPTIONS} />
                    </Form.Item>
                  </Col>
                  <Col xs={12} md={8}>
                    <Form.Item label="Source Column" name="sourceColumn"
                      tooltip="Which line area feeds an area-based formula: Actual, Chargeable, or None.">
                      <Select options={SOURCE_COLUMN_OPTIONS} />
                    </Form.Item>
                  </Col>
                  <Col xs={12} md={8}>
                    <Form.Item label="UOM" name="uom"><Select options={UOM_OPTIONS} /></Form.Item>
                  </Col>
                  <Col xs={12} md={8}>
                    <Form.Item label="Quantity Basis" name="quantityBasis"
                      tooltip="For per-piece charges: count all line qty, or only T- prefixed lines.">
                      <Select options={QUANTITY_BASIS_OPTIONS} />
                    </Form.Item>
                  </Col>
                  <Col xs={12} md={8}>
                    <Form.Item label="Show Result" name="showResultMode">
                      <Select options={SHOW_RESULT_OPTIONS} />
                    </Form.Item>
                  </Col>
                  <Col xs={12} md={8}>
                    <Form.Item label="Group" name="displayGroup"
                      tooltip="Optional display grouping (e.g. ENERGY_SURCHARGE).">
                      <Input maxLength={60} placeholder="e.g. ENERGY_SURCHARGE" allowClear />
                    </Form.Item>
                  </Col>
                </Row>
              </Section>
            </div>

            {/* Step 4 · Area Slabs */}
            <div style={show(3)}>
              <Section title="Area Slabs">
                <Row gutter={16}>
                  <Col xs={12} md={6}><Form.Item label="Slab 1 Area" name="slab1Area"><InputNumber min={0} step={0.0001} style={{ width: '100%' }} addonAfter="SQM" /></Form.Item></Col>
                  <Col xs={12} md={6}><Form.Item label="Slab 1 Value" name="slab1Value"><InputNumber min={0} step={0.01} style={{ width: '100%' }} /></Form.Item></Col>
                  <Col xs={12} md={6}><Form.Item label="Slab 2 Area" name="slab2Area"><InputNumber min={0} step={0.0001} style={{ width: '100%' }} addonAfter="SQM" /></Form.Item></Col>
                  <Col xs={12} md={6}><Form.Item label="Slab 2 Value" name="slab2Value"><InputNumber min={0} step={0.01} style={{ width: '100%' }} /></Form.Item></Col>
                  <Col xs={12} md={6}><Form.Item label="Slab 3 Area" name="slab3Area"><InputNumber min={0} step={0.0001} style={{ width: '100%' }} addonAfter="SQM" /></Form.Item></Col>
                  <Col xs={12} md={6}><Form.Item label="Slab 3 Value" name="slab3Value"><InputNumber min={0} step={0.01} style={{ width: '100%' }} /></Form.Item></Col>
                </Row>
              </Section>
            </div>
          </div>
        </div>
      </Form>
    </Modal>
  );
}
